import { LegalPage } from "../components/LegalPage";

const LAST_UPDATED = "2026-10-22";

const SECTIONS = [
  {
    title: "About these Terms",
    body: ({ email }) => (
      <>
        <p>
          These Terms &amp; Conditions ("Terms") govern the services that Elin's Consultancy
          ("we", "us", "our") provides to clients ("you") in England. By engaging us —
          whether through our website, by email, over the phone, or in person — you agree
          to be bound by these Terms. If anything here is unclear, please contact us at{" "}
          <a href={`mailto:${email}`}>{email}</a> before proceeding.
        </p>
        <p>
          Some engagements are confirmed in a separate written agreement or scope-of-work.
          Where that written agreement conflicts with these Terms, the written agreement
          takes precedence for that engagement only.
        </p>
      </>
    ),
  },
  {
    title: "Our Services",
    body: () => (
      <>
        <p>
          Elin's Consultancy provides independent guidance on residential property matters,
          including (but not limited to) property sourcing for investors, HMO licensing
          support, Stamp Duty Land Tax guidance, refurbishment and development project
          introductions, and tenant and leaseholder advice.
        </p>
        <p>
          We are a consultancy, not a legal firm, tax adviser, mortgage broker or regulated
          valuer. Where your matter requires regulated advice, we will introduce you to a
          suitably qualified professional in our network. We do not accept referral
          commissions in a way that could compromise independent advice to you.
        </p>
        <p>
          Any projections, yield figures or price indications we share are illustrative
          only. Property prices and rental income can fall as well as rise, and past
          performance is not a guide to future outcomes.
        </p>
      </>
    ),
  },
  {
    title: "Your Responsibilities",
    body: () => (
      <>
        <p>You agree to:</p>
        <ul>
          <li>Provide accurate, complete and up-to-date information when you engage us or any professional we introduce.</li>
          <li>Tell us promptly of any change that could affect our work (new ownership, finance, timeline, or intended use of the property).</li>
          <li>Make your own decisions on any purchase, sale, let, refinance or legal action — our role is to inform, not to decide for you.</li>
          <li>Comply with all applicable laws, including the Housing Act 2004, HMO licensing rules in your council area, the Landlord &amp; Tenant Acts, and tax obligations.</li>
        </ul>
        <p>
          Failure to provide accurate information may affect the usefulness of our advice
          and may result in additional fees to redo work.
        </p>
      </>
    ),
  },
  {
    title: "Fees and Payment",
    body: () => (
      <>
        <p>
          Our fees for each engagement are set out in advance in writing, either in a
          proposal, scope-of-work or by email. We do not charge for an initial
          introductory conversation.
        </p>
        <p>
          Unless otherwise agreed in writing, fees are payable within <strong>14 days</strong>{" "}
          of the date of our invoice. Where we are VAT‑registered our invoices will clearly
          show our VAT number and apply VAT at the prevailing rate; before we are
          VAT‑registered, no VAT is charged.
        </p>
        <p>
          Overdue amounts may be charged statutory interest and reasonable recovery costs
          under the Late Payment of Commercial Debts (Interest) Act 1998. Reasonable
          disbursements (e.g. search fees, filing fees, third‑party reports) are passed on
          at cost and shown separately on invoices.
        </p>
        <p>
          Deposits are non‑refundable unless we are unable to perform the engagement for
          reasons wholly within our control. Either party may terminate an engagement on
          reasonable written notice; fees for work completed up to the date of termination
          remain payable.
        </p>
      </>
    ),
  },
  {
    title: "Limitation of Liability",
    body: () => (
      <>
        <p>
          Nothing in these Terms limits our liability for death or personal injury caused
          by our negligence, for fraud or fraudulent misrepresentation, or for any other
          liability that cannot lawfully be limited.
        </p>
        <p>
          Subject to the above, our total liability arising out of or in connection with an
          engagement (whether in contract, tort — including negligence — breach of
          statutory duty, or otherwise) is limited to the <strong>fees paid by you to us
          for that engagement in the 12 months before the claim arose</strong>.
        </p>
        <p>
          We are not liable for indirect or consequential loss, loss of profit, loss of
          business opportunity, loss of goodwill, or loss arising from decisions you take
          in reliance on information from third parties (including introduced professionals).
          Nothing in these Terms affects your statutory rights where you are a consumer.
        </p>
      </>
    ),
  },
  {
    title: "Intellectual Property",
    body: () => (
      <>
        <p>
          All content on our website, in our reports and in our deliverables — including
          text, graphics, logos, calculators and the "Elin's Consultancy" brand — is owned
          by us or our licensors and is protected by UK and international copyright and
          trademark law.
        </p>
        <p>
          Where we deliver a bespoke report or document for you, you receive a non‑exclusive,
          non‑transferable licence to use that output for your own property matter.
          You may not publish, resell or redistribute it without our prior written consent.
        </p>
      </>
    ),
  },
  {
    title: "Confidentiality",
    body: () => (
      <>
        <p>
          We treat the information you share with us as confidential and only use it to
          deliver the engagement, to introduce the professionals you ask us to, or where
          disclosure is required by law or regulation. See our{" "}
          <a href="/privacy-policy">Privacy Policy</a> for details on how we handle
          personal data.
        </p>
      </>
    ),
  },
  {
    title: "Third-Party Introductions",
    body: () => (
      <>
        <p>
          Where we introduce you to a solicitor, accountant, broker, surveyor,
          developer or other third party, those professionals are engaged by you directly
          and are responsible to you under their own terms. We take care in choosing our
          network but we are not liable for their work, fees or conduct.
        </p>
      </>
    ),
  },
  {
    title: "Website Use",
    body: () => (
      <>
        <p>
          Our website is provided for general information. The Stamp Duty Calculator, HMO
          Licensing Checker and Rentable Yield Snapshot are guidance tools only and are
          not a substitute for professional tax, legal or valuation advice.
        </p>
        <p>
          We take reasonable steps to keep the site available and accurate, but we do not
          warrant it will be uninterrupted or error‑free. You use the site at your own
          risk and must not attempt to disrupt, probe or misuse it.
        </p>
      </>
    ),
  },
  {
    title: "Governing Law and Jurisdiction",
    body: () => (
      <>
        <p>
          These Terms, each engagement and any dispute or claim arising out of them (including
          non‑contractual disputes) are governed by the law of <strong>England and Wales</strong>.
          The courts of England and Wales have exclusive jurisdiction to settle any such
          dispute, except that where you are a consumer, you may also bring proceedings in
          the courts of the country in which you reside.
        </p>
      </>
    ),
  },
  {
    title: "Changes to these Terms",
    body: () => (
      <>
        <p>
          We may update these Terms from time to time to reflect changes in the law or our
          services. The "Last updated" date at the top shows when the current version came
          into effect. The current version always applies — if you have engaged us under an
          earlier version, the version in force on the date of your engagement letter will
          continue to apply to that engagement.
        </p>
      </>
    ),
  },
];

const TermsAndConditions = () => (
  <LegalPage
    metaTitle="Terms & Conditions | Elin's Consultancy"
    metaDescription="The terms on which Elin's Consultancy provides property guidance to investors, tenants and leaseholders in England."
    eyebrow="Legal"
    titleLines={[
      "Terms &",
      <>
        <em className="italic font-normal text-gold-soft">Conditions</em>
      </>,
    ]}
    intro="The terms on which we work with you — plainly written, with a few lines of legal where they matter."
    lastUpdated={LAST_UPDATED}
    sections={SECTIONS}
  />
);

export default TermsAndConditions;
